import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import EvidenceUpload from '../../dispute-resolution/components/EvidenceUpload';
import { violationChargeService } from '../../../services/violationChargeService';


const DisputeForm = ({ charge, onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    disputeReason: '',
    customReason: '',
    detailedExplanation: '',
    incidentTimeline: '',
  });
  const [evidence, setEvidence] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const disputeReasons = [
    { value: 'no_damage', label: 'No damage occurred' },
    { value: 'pre_existing', label: 'Pre-existing condition' },
    { value: 'unfair_assessment', label: 'Unfair assessment' },
    { value: 'policy_misinterpretation', label: 'Policy misinterpretation' },
    { value: 'incorrect_amount', label: 'Incorrect charge amount' },
    { value: 'not_responsible', label: 'Not responsible for damage' },
    { value: 'other', label: 'Other (specify below)' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    // Validation
    if (!formData?.disputeReason) {
      setError('Please select a dispute reason');
      return;
    }

    if (formData?.disputeReason === 'other' && !formData?.customReason?.trim()) {
      setError('Please specify your custom dispute reason');
      return;
    }

    if (!formData?.detailedExplanation?.trim()) {
      setError('Please provide a detailed explanation');
      return;
    }

    if (evidence?.length === 0) {
      setError('Please upload at least one piece of evidence');
      return;
    }

    try {
      setIsSubmitting(true);

      // Upload evidence files
      const evidenceFiles = evidence?.map(e => e?.file);
      const evidenceUrls = await violationChargeService?.uploadDocumentation(evidenceFiles);

      // Prepare dispute reason text
      const reasonLabel = disputeReasons?.find(r => r?.value === formData?.disputeReason)?.label;
      const finalReason = formData?.disputeReason === 'other' 
        ? formData?.customReason 
        : reasonLabel;

      const disputeText = `${finalReason}\n\nDetailed Explanation:\n${formData?.detailedExplanation}${formData?.incidentTimeline ? `\n\nTimeline:\n${formData?.incidentTimeline}` : ''}\n\nEvidence: ${evidenceUrls?.length} file(s) uploaded`;

      // Update charge status to disputed
      await violationChargeService?.updateChargeStatus(
        charge?.id,
        'disputed',
        disputeText
      );

      // Fetch updated charge
      const updatedCharges = await violationChargeService?.getRenterCharges();
      const updatedCharge = updatedCharges?.find(c => c?.id === charge?.id);

      onSubmit?.(updatedCharge || { ...charge, chargeStatus: 'disputed', disputeReason: disputeText });
    } catch (err) {
      console.error('Submit dispute error:', err);
      setError(err?.message || 'Failed to submit dispute');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-smooth mb-6"
      >
        <Icon name="ArrowLeft" size={20} strokeWidth={2} />
        <span className="font-body text-sm">Back to Charge Selection</span>
      </button>

      {/* Charge Summary */}
      <div className="bg-muted/30 border border-border rounded-lg p-6 mb-6">
        <h2 className="font-heading text-xl font-bold text-foreground mb-4">
          Disputing Charge: {charge?.chargeReference}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Charge Amount</p>
            <p className="font-body text-lg font-bold text-foreground">${charge?.chargeAmount?.toFixed(2)}</p>
          </div>
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Violation Reason</p>
            <p className="font-body text-sm text-foreground">{charge?.violationReason}</p>
          </div>
          <div>
            <p className="font-caption text-xs text-muted-foreground mb-1">Issued Date</p>
            <p className="font-body text-sm text-foreground">
              {new Date(charge?.createdAt)?.toLocaleDateString()}
            </p>
          </div>
        </div>
        {charge?.violationDescription && (
          <div className="mt-4 pt-4 border-t border-border">
            <p className="font-caption text-xs text-muted-foreground mb-1">Owner's Description</p>
            <p className="font-body text-sm text-foreground">{charge?.violationDescription}</p>
          </div>
        )}
      </div>

      {/* Dispute Form */}
      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6 space-y-6">
        <div>
          <h3 className="font-heading text-xl font-bold text-foreground mb-4">
            File Your Dispute
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            Provide detailed information and evidence to support your dispute claim.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
            <p className="font-body text-sm text-error">{error}</p>
          </div>
        )}

        {/* Dispute Reason Selection */}
        <div>
          <label className="block font-body font-semibold text-foreground mb-3">
            Dispute Reason <span className="text-error">*</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {disputeReasons?.map((reason) => (
              <label
                key={reason?.value}
                className={`flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-smooth ${
                  formData?.disputeReason === reason?.value
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <input
                  type="radio"
                  name="disputeReason"
                  value={reason?.value}
                  checked={formData?.disputeReason === reason?.value}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="font-body text-sm text-foreground">{reason?.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Custom Reason (if Other selected) */}
        {formData?.disputeReason === 'other' && (
          <div>
            <label className="block font-body font-semibold text-foreground mb-2">
              Specify Your Reason <span className="text-error">*</span>
            </label>
            <input
              type="text"
              name="customReason"
              value={formData?.customReason}
              onChange={handleInputChange}
              placeholder="Enter your custom dispute reason"
              className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        )}

        {/* Detailed Explanation */}
        <div>
          <label className="block font-body font-semibold text-foreground mb-2">
            Detailed Explanation <span className="text-error">*</span>
          </label>
          <textarea
            name="detailedExplanation"
            value={formData?.detailedExplanation}
            onChange={handleInputChange}
            rows={6}
            placeholder="Provide a comprehensive explanation of why you are disputing this charge. Include specific details, circumstances, and any relevant context."
            className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        {/* Incident Timeline */}
        <div>
          <label className="block font-body font-semibold text-foreground mb-2">
            Incident Timeline (Optional)
          </label>
          <textarea
            name="incidentTimeline"
            value={formData?.incidentTimeline}
            onChange={handleInputChange}
            rows={4}
            placeholder="Provide a chronological timeline of events related to this charge (e.g., pickup time, usage period, return inspection, etc.)"
            className="w-full px-4 py-3 bg-background border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
        </div>

        {/* Evidence Upload */}
        <div>
          <label className="block font-body font-semibold text-foreground mb-3">
            Supporting Evidence <span className="text-error">*</span>
          </label>
          <p className="font-body text-sm text-muted-foreground mb-4">
            Upload photos, videos, documents, or any other evidence that supports your dispute claim.
          </p>
          <EvidenceUpload evidence={evidence} onChange={setEvidence} />
        </div>

        {/* Submit Button */}
        <div className="flex items-center space-x-4 pt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            iconName="Send"
            className="flex-1"
          >
            {isSubmitting ? 'Submitting Dispute...' : 'Submit Dispute'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DisputeForm;